import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Button,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Updated import
import {
  DivisionsDropdown,
  AdminUsersDropdown,
} from "../../CourseCatalog/DropDowns";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, termDetail, onRecordUpdated }) => {
  // Use useFirestore hook for "Fps" collection
  const {
    updateData,
    deleteData,
    loading: firestoreLoading,
    error: firestoreError,
  } = useFirestore("Fps");

  // Use useFirestore hook for "FpsCourses" collection to get available courses
  const { data: coursesData, loading: coursesLoading } =
    useFirestore("FpsCourses");

  // Use useFirestore hook for "Divisions" collection to get available divisions
  const { data: divisionsData, loading: divisionsLoading } =
    useFirestore("Divisions");

  // Use useFirestore hook for "users" collection to get admin users
  const { data: usersData, loading: usersLoading } = useFirestore("users");

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [formData, setFormData] = useState({
    Active: 0,
    Last_Name: "",
    First_name: "",
    Full_Name: "",
    Courses: "",
    Career_Days: 0,
    Sinclair_Department: "",
    Title: "",
    Sinclair_Location: "",
    Sinclair_Phone: "",
    Sinclair_Email: "",
    Project_Manager_Name: "",
    Project_Manager_Id: "",
    Sinclair_Division: "",
    Sinclair_Division_abv: "",
    Note: "",
    Tartan_Id: "",
  });
  const [loading, setLoading] = useState(false);
  // Snackbar for "Record has been updated" is now handled by Fps.js
  // const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [tartanIdError, setTartanIdError] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (termDetail) {
      const courses = termDetail.Courses || "";
      const coursesArray = courses
        ? courses.split(", ").filter((c) => c.trim())
        : [];

      setFormData({
        Active: termDetail.Active, // Assuming termDetail.Active is -1 or 0 from Firestore
        Last_Name: termDetail.Last_Name || "",
        First_name: termDetail.First_name || "",
        Full_Name: termDetail.Full_Name || "",
        Courses: termDetail.Courses || "",
        Career_Days: termDetail.Career_Days, // Assuming termDetail.Career_Days is -1 or 0 from Firestore
        Sinclair_Department: termDetail.Sinclair_Department || "",
        Title: termDetail.Title || "",
        Sinclair_Location: termDetail.Sinclair_Location || "",
        Sinclair_Phone: termDetail.Sinclair_Phone || "",
        Sinclair_Email: termDetail.Sinclair_Email || "",
        Project_Manager_Name: termDetail.Project_Manager_Name || "",
        Project_Manager_Id: termDetail.Project_Manager_Id || "",
        Sinclair_Division: termDetail.Sinclair_Division || "",
        Sinclair_Division_abv: termDetail.Sinclair_Division_abv || "",
        Note: termDetail.Note || "",
        Tartan_Id: termDetail.Tartan_Id || "",
      });
      setSelectedCourses(coursesArray);
    }
  }, [termDetail]);

  // Auto-populate division abbreviation when editing existing record
  useEffect(() => {
    if (termDetail && divisionsData && formData.Sinclair_Division) {
      const selectedDivision = divisionsData.find((division) => {
        const divisionName =
          division.DivisionName || division.Division_Name || division.Name;
        return (
          divisionName &&
          divisionName.trim().toLowerCase() ===
            formData.Sinclair_Division.trim().toLowerCase()
        );
      });

      if (selectedDivision) {
        const abbreviation =
          selectedDivision?.Abbreviation ||
          selectedDivision?.abbreviation ||
          selectedDivision?.DivisionAbbreviation ||
          selectedDivision?.Division_Abbreviation ||
          selectedDivision?.DivisionCode ||
          selectedDivision?.Division_Code ||
          selectedDivision?.DivisionID ||
          selectedDivision?.DivisionId ||
          selectedDivision?.Division_Id ||
          selectedDivision?.Code ||
          "";
        // Only update if the abbreviation is different to avoid infinite loops
        if (formData.Sinclair_Division_abv !== abbreviation) {
          setFormData((prevData) => ({
            ...prevData,
            Sinclair_Division_abv: abbreviation,
          }));
        }
      }
    }
  }, [termDetail, divisionsData, formData.Sinclair_Division]);

  // Auto-populate Full_Name when First_name or Last_Name changes
  useEffect(() => {
    const fullName = `${formData.First_name} ${formData.Last_Name}`.trim();
    if (fullName !== formData.Full_Name) {
      setFormData((prevData) => ({
        ...prevData,
        Full_Name: fullName,
      }));
    }
  }, [formData.First_name, formData.Last_Name]);

  // Update Courses field when selectedCourses changes
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      Courses: selectedCourses.join(", "),
    }));
  }, [selectedCourses]);

  // Auto-populate Pathway Manager ID when editing existing record
  useEffect(() => {
    if (
      termDetail &&
      usersData &&
      formData.Project_Manager_Name &&
      !formData.Project_Manager_Id
    ) {
      const selectedManager = usersData.find(
        (user) =>
          user.name === formData.Project_Manager_Name &&
          (user.techPrepAdminFlag === true || user.userTypes?.admin === true),
      );

      if (selectedManager) {
        setFormData((prevData) => ({
          ...prevData,
          Project_Manager_Id: selectedManager.id,
        }));
      }
    }
  }, [
    termDetail,
    usersData,
    formData.Project_Manager_Name,
    formData.Project_Manager_Id,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle Sinclair Division change to auto-populate abbreviation
    if (name === "Sinclair_Division" && divisionsData) {
      let abbreviation = "";

      if (value) {
        const selectedDivision = divisionsData.find((division) => {
          const divisionName =
            division.DivisionName || division.Division_Name || division.Name;
          return (
            divisionName &&
            divisionName.trim().toLowerCase() === value.trim().toLowerCase()
          );
        });

        // Look for various abbreviation field names
        abbreviation =
          selectedDivision?.Abbreviation ||
          selectedDivision?.abbreviation ||
          selectedDivision?.DivisionAbbreviation ||
          selectedDivision?.Division_Abbreviation ||
          selectedDivision?.DivisionCode ||
          selectedDivision?.Division_Code ||
          selectedDivision?.DivisionID ||
          selectedDivision?.DivisionId ||
          selectedDivision?.Division_Id ||
          selectedDivision?.Code ||
          "";
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        Sinclair_Division_abv: abbreviation,
      }));
    }
    // Handle Pathway Manager Name change to auto-populate ID
    else if (name === "Project_Manager_Name" && usersData) {
      let managerId = "";

      if (value) {
        const selectedManager = usersData.find(
          (user) =>
            user.name === value &&
            (user.techPrepAdminFlag === true || user.userTypes?.admin === true),
        );

        managerId = selectedManager?.id || "";
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        Project_Manager_Id: managerId,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
      // Validate Tartan ID length inline
      if (name === "Tartan_Id") {
        setTartanIdError(
          value && value.trim().length > 0 && value.trim().length !== 7
            ? "Tartan ID must be exactly 7 digits"
            : "",
        );
      }
    }
  };

  const handleCoursesChange = (event) => {
    const value = event.target.value;
    setSelectedCourses(typeof value === "string" ? value.split(",") : value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    // Validate Tartan ID before submitting
    const tartanVal = formData.Tartan_Id ? formData.Tartan_Id.trim() : "";
    if (tartanVal.length === 0) {
      setTartanIdError("Tartan ID is required (7 digits)");
      return;
    }
    if (tartanVal.length !== 7) {
      setTartanIdError("Tartan ID must be exactly 7 digits");
      return;
    }
    setTartanIdError("");
    setLoading(true);
    try {
      // termDetail.id should be the Firestore document ID
      await updateData(termDetail.id, formData);
      setLoading(false);
      // setSnackbarOpen(true); // Shows "Record has been updated" - Handled by Fps.js
      onRecordUpdated(); // Call the callback function to refresh the list and close dialog in Fps.js
    } catch (error) {
      console.error("Error updating record:", error);
      // alert(`Error updating record: ${firestoreError || error.message}`);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setConfirmDeleteOpen(false); // Close confirmation dialog
    try {
      await deleteData(termDetail.id); // termDetail.id should be the Firestore document ID
      setLoading(false);
      // Snackbar for delete can be handled by Fps.js via onRecordUpdated
      onRecordUpdated(); // This will trigger snackbar in Fps.js and refresh list
    } catch (error) {
      console.error("Error deleting record:", error);
      // alert(`Error deleting record: ${firestoreError || error.message}`);
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleActualDelete = () => {
    // Renamed to avoid conflict with handleDelete from hook
    handleDelete();
  };

  // const handleSnackbarClose = () => {
  //   setSnackbarOpen(false);
  // };

  if (!termDetail) {
    return null; // Or some loading/placeholder if the dialog is open without details
  }

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="Last_Name"
                value={formData.Last_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="First_name"
                value={formData.First_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="Full_Name"
                value={formData.Full_Name}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-generated from First and Last Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="courses-select-label">Courses</InputLabel>
                <Select
                  labelId="courses-select-label"
                  id="courses-select"
                  multiple
                  value={selectedCourses}
                  onChange={handleCoursesChange}
                  label="Courses"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={coursesLoading}
                >
                  {coursesLoading && (
                    <MenuItem disabled>Loading courses...</MenuItem>
                  )}
                  {!coursesLoading &&
                    (!coursesData || coursesData.length === 0) && (
                      <MenuItem disabled>No courses available</MenuItem>
                    )}
                  {coursesData &&
                    coursesData
                      .filter((course) => course.Active === "Yes") // Only show courses where Active is "Yes"
                      .sort((a, b) =>
                        (a.Course || "").localeCompare(b.Course || ""),
                      ) // Sort alphabetically by Course name
                      .map((course) => (
                        <MenuItem key={course.id} value={course.Course}>
                          {course.Course}{" "}
                          {course.Subject ? `- ${course.Subject}` : ""}
                        </MenuItem>
                      ))}
                </Select>
                {coursesLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sinclair Department"
                name="Sinclair_Department"
                value={formData.Sinclair_Department}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                name="Title"
                value={formData.Title}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sinclair Location"
                name="Sinclair_Location"
                value={formData.Sinclair_Location}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sinclair Phone"
                name="Sinclair_Phone"
                value={formData.Sinclair_Phone}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sinclair Email"
                name="Sinclair_Email"
                value={formData.Sinclair_Email}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AdminUsersDropdown
                value={formData.Project_Manager_Name}
                onChange={handleChange}
                name="Project_Manager_Name"
                label="Pathway Manager Name"
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway Manager ID"
                name="Project_Manager_Id"
                value={formData.Project_Manager_Id}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-populated based on manager selection"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DivisionsDropdown
                value={formData.Sinclair_Division}
                onChange={handleChange}
                name="Sinclair_Division"
                label="Sinclair Division"
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sinclair Division Abbreviation"
                name="Sinclair_Division_abv"
                value={formData.Sinclair_Division_abv}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-populated based on division selection"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Note"
                name="Note"
                value={formData.Note}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tartan ID"
                name="Tartan_Id"
                value={formData.Tartan_Id}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!tartanIdError}
                helperText={tartanIdError || "Must be exactly 7 digits"}
                inputProps={{ maxLength: 7 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === -1}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Career_Days === -1}
                    onChange={handleCheckboxChange}
                    name="Career_Days"
                  />
                }
                label="Career Days"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          saveDisabled={firestoreLoading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading || firestoreLoading}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleActualDelete}
        deleting={loading || firestoreLoading}
        message="Are you sure you want to delete this record?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar is now handled by Fps.js
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Record has been updated" // This message might need to be dynamic if delete also uses it
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "black",
            color: "white",
          },
        }}
      />
      */}
    </>
  );
};

export default Edit;
